import { playSegment, toggleAtBubble } from './player';
import { saveTranscript, saveUndoState, state } from './state';
import type { DeepgramResponse, Paragraph } from './types';
import { $, confirmModal, formatTime, showToast } from './ui';

const SPEAKER_BG = ['bg-primary', 'bg-secondary', 'bg-blue-500', 'bg-green-500', 'bg-pink-500'];
const SPEAKER_TEXT = [
  'text-primary',
  'text-secondary',
  'text-blue-500',
  'text-green-500',
  'text-pink-500',
];
const SPEAKER_BORDER = [
  'border-primary',
  'border-secondary',
  'border-blue-500',
  'border-green-500',
  'border-pink-500',
];

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function colorIndexFor(speaker: number): number {
  const speakers = Object.keys(state.speakerMap);
  const idx = speakers.indexOf(speaker.toString());
  return (idx >= 0 ? idx : 0) % SPEAKER_BG.length;
}

function closeAnySpeakerPicker(): void {
  for (const el of document.querySelectorAll('.speaker-picker')) el.remove();
}

interface SpeakerPickerOptions {
  exclude?: number;
  allowNew?: boolean;
  newLabel?: string;
}

function showSpeakerPicker(
  anchor: HTMLElement,
  opts: SpeakerPickerOptions,
  onPick: (speaker: number) => void
): void {
  closeAnySpeakerPicker();

  const picker = document.createElement('div');
  picker.className =
    'speaker-picker fixed z-[2000] bg-surface-bright border border-outline-variant rounded-xl shadow-premium p-2 flex flex-col gap-1 min-w-[200px]';

  const speakers = Object.keys(state.speakerMap)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((s) => opts.exclude === undefined || s !== opts.exclude);

  if (speakers.length === 0 && !opts.allowNew) {
    picker.innerHTML = `<div class="px-3 py-2 text-sm text-on-surface-variant">No hay otros speakers</div>`;
  } else {
    for (const s of speakers) {
      const colorIdx = colorIndexFor(s);
      const bg = SPEAKER_BG[colorIdx];
      const name = state.speakerMap[s] ?? `Speaker ${s}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-container-low text-left text-sm w-full text-on-surface';
      btn.innerHTML = `<div class="w-3 h-3 rounded-full ${bg} flex-shrink-0"></div><span class="truncate">${escapeHtml(name)}</span>`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        picker.remove();
        onPick(s);
      });
      picker.appendChild(btn);
    }

    if (opts.allowNew) {
      const newBtn = document.createElement('button');
      newBtn.type = 'button';
      newBtn.className =
        'flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-container-low text-left text-sm w-full text-on-surface border-t border-outline-variant/30 mt-1 pt-2';
      newBtn.innerHTML = `<span class="material-symbols-outlined text-base">add</span><span>${escapeHtml(opts.newLabel ?? 'Nuevo speaker')}</span>`;
      newBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const existing = Object.keys(state.speakerMap).map(Number);
        const next = existing.length > 0 ? Math.max(...existing) + 1 : 0;
        state.speakerMap[next] = `Speaker ${next}`;
        picker.remove();
        onPick(next);
      });
      picker.appendChild(newBtn);
    }
  }

  document.body.appendChild(picker);

  const rect = anchor.getBoundingClientRect();
  const pickerRect = picker.getBoundingClientRect();
  let top = rect.bottom + 4;
  let left = rect.left;
  if (left + pickerRect.width > window.innerWidth - 8) {
    left = Math.max(8, window.innerWidth - pickerRect.width - 8);
  }
  if (top + pickerRect.height > window.innerHeight - 8) {
    top = Math.max(8, rect.top - pickerRect.height - 4);
  }
  picker.style.top = `${top}px`;
  picker.style.left = `${left}px`;

  setTimeout(() => {
    function onOutside(e: MouseEvent): void {
      if (!picker.contains(e.target as Node)) {
        picker.remove();
        document.removeEventListener('click', onOutside);
        document.removeEventListener('keydown', onEsc);
      }
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        picker.remove();
        document.removeEventListener('click', onOutside);
        document.removeEventListener('keydown', onEsc);
      }
    }
    document.addEventListener('click', onOutside);
    document.addEventListener('keydown', onEsc);
  }, 0);
}

function reassignBubble(index: number, newSpeaker: number): void {
  const para = state.paragraphs[index];
  if (!para || para.speaker === newSpeaker) return;
  saveUndoState();
  para.speaker = newSpeaker;
  saveTranscript();
  renderSpeakers();
  renderTranscript();
}

function effectiveEnd(idx: number): number | undefined {
  const p = state.paragraphs[idx];
  if (!p) return undefined;
  if (typeof p.end === 'number') return p.end;
  const next = state.paragraphs[idx + 1];
  if (next) return Math.max(p.start + 0.5, next.start - 0.05);
  return undefined;
}

function splitBubble(index: number): void {
  const para = state.paragraphs[index];
  if (!para) return;
  saveUndoState();
  const clone = { ...para };
  state.paragraphs.splice(index + 1, 0, clone);
  const shifted = new Set<number>();
  for (const i of state.selectedBubbles) {
    shifted.add(i > index ? i + 1 : i);
  }
  state.selectedBubbles = shifted;
  saveTranscript();
  renderTranscript();
}

async function deleteBubble(index: number): Promise<void> {
  const para = state.paragraphs[index];
  if (!para) return;
  const preview = para.text.length > 80 ? `${para.text.slice(0, 80)}…` : para.text;
  const ok = await confirmModal({
    title: 'Borrar bocadillo',
    message: `¿Borrar este bocadillo?\n\n"${preview}"`,
    confirmLabel: 'Borrar',
    cancelLabel: 'Cancelar',
    danger: true,
  });
  if (!ok) return;
  saveUndoState();
  state.paragraphs.splice(index, 1);
  const newSelected = new Set<number>();
  for (const i of state.selectedBubbles) {
    if (i < index) newSelected.add(i);
    else if (i > index) newSelected.add(i - 1);
  }
  state.selectedBubbles = newSelected;
  saveTranscript();
  renderTranscript();
}

async function mergeSpeakers(origin: number, target: number): Promise<void> {
  if (origin === target) return;
  const moving = state.paragraphs.filter((p) => p.speaker === origin).length;
  const originName = state.speakerMap[origin] ?? `Speaker ${origin}`;
  const targetName = state.speakerMap[target] ?? `Speaker ${target}`;
  const ok = await confirmModal({
    title: 'Fusionar speakers',
    message: `¿Fusionar "${originName}" con "${targetName}"? Se moverán ${moving} bocadillo${moving === 1 ? '' : 's'}.`,
    confirmLabel: 'Fusionar',
    cancelLabel: 'Cancelar',
  });
  if (!ok) return;
  saveUndoState();
  for (const p of state.paragraphs) {
    if (p.speaker === origin) p.speaker = target;
  }
  delete state.speakerMap[origin];
  saveTranscript();
  renderSpeakers();
  renderTranscript();
}

export function processDeepgramResponse(data: DeepgramResponse): void {
  if (data.metadata) {
    const durationMins = data.metadata.duration / 60;
    const cost = durationMins * 0.0043;
    console.log(
      `%c=== ESTADÍSTICAS ===\nDuración: ${durationMins.toFixed(2)} min\nCoste Deepgram (Nova-2): $${cost.toFixed(4)}\n==================`,
      'color:#3b82f6;font-weight:bold;'
    );
  }

  const words = data.results.channels[0]?.alternatives[0]?.words ?? [];
  if (words.length === 0) {
    showToast('No se detectaron palabras en el audio.', 'warning');
    return;
  }

  let currentSpeaker = words[0].speaker ?? 0;
  let currentParagraph = [words[0]];
  const paragraphs: Paragraph[] = [];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const speaker = word.speaker ?? 0;
    if (speaker === currentSpeaker) {
      currentParagraph.push(word);
    } else {
      paragraphs.push({
        speaker: currentSpeaker,
        start: currentParagraph[0].start,
        end: currentParagraph[currentParagraph.length - 1].end,
        text: currentParagraph.map((w) => w.punctuated_word ?? w.word).join(' '),
      });
      currentSpeaker = speaker;
      currentParagraph = [word];
    }
  }
  if (currentParagraph.length > 0) {
    paragraphs.push({
      speaker: currentSpeaker,
      start: currentParagraph[0].start,
      end: currentParagraph[currentParagraph.length - 1].end,
      text: currentParagraph.map((w) => w.punctuated_word ?? w.word).join(' '),
    });
  }

  state.paragraphs = paragraphs;
  const uniqueSpeakers = [...new Set(paragraphs.map((p) => p.speaker))].sort((a, b) => a - b);
  state.speakerMap = {};
  for (const s of uniqueSpeakers) {
    state.speakerMap[s] = `Speaker ${s}`;
  }
}

export function renderTranscript(): void {
  const container = $('transcriptContent');
  container.innerHTML = '';
  if (state.paragraphs.length === 0) return;

  const alignMap: Record<number, 'left' | 'right'> = {};
  let isRight = false;

  state.paragraphs.forEach((para, index) => {
    if (alignMap[para.speaker] === undefined) {
      alignMap[para.speaker] = isRight ? 'right' : 'left';
      isRight = !isRight;
    }

    const colorIdx = colorIndexFor(para.speaker);
    const bg = SPEAKER_BG[colorIdx];
    const txt = SPEAKER_TEXT[colorIdx];
    const border = SPEAKER_BORDER[colorIdx];
    const alignment = alignMap[para.speaker];
    const isSelected = state.selectedBubbles.has(index);
    const speakerName = state.speakerMap[para.speaker] ?? `Speaker ${para.speaker}`;
    const initial = speakerName.charAt(0).toUpperCase();

    const div = document.createElement('div');
    div.className = `flex gap-4 group w-full mb-6 ${
      alignment === 'right' ? 'flex-row-reverse pr-4 md:pr-12' : 'pl-4 md:pl-12'
    } ${state.isEditMode && isSelected ? 'bubble-selected' : ''} ${
      !state.isEditMode ? 'bubble-clickable' : ''
    }`;
    div.dataset.bubbleIndex = String(index);
    div.dataset.start = String(para.start);

    if (!state.isEditMode) {
      div.addEventListener('click', () => toggleAtBubble(index, para.start));
    }

    if (state.isEditMode) {
      div.draggable = true;
      div.classList.add('bubble-draggable');
      div.dataset.index = String(index);

      div.addEventListener('dragstart', (e) => {
        e.dataTransfer?.setData('text/plain', String(index));
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      });
      div.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        div.classList.add('drag-over');
      });
      div.addEventListener('dragleave', () => div.classList.remove('drag-over'));
      div.addEventListener('drop', (e) => {
        e.preventDefault();
        div.classList.remove('drag-over');
        const sourceIndex = Number(e.dataTransfer?.getData('text/plain') ?? -1);
        if (Number.isFinite(sourceIndex) && sourceIndex !== index) {
          mergeBubbles(sourceIndex, index);
        }
      });
    }

    const checkboxHtml = state.isEditMode
      ? `<div class="flex items-center justify-center mr-2 ${alignment === 'right' ? 'order-last ml-2 mr-0' : ''}">
          <input type="checkbox" class="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer select-bubble-cb" data-index="${index}" ${isSelected ? 'checked' : ''}>
        </div>`
      : '';

    const editActionsHtml = state.isEditMode
      ? `<button type="button" class="reassign-bubble-btn flex items-center justify-center w-8 h-8 rounded-full hover:bg-primary/15 text-primary transition-all" data-index="${index}" title="Cambiar speaker de este bocadillo">
          <span class="material-symbols-outlined text-[18px]">swap_horiz</span>
        </button>
        <button type="button" class="split-bubble-btn flex items-center justify-center w-8 h-8 rounded-full hover:bg-amber-500/15 text-amber-600 transition-all" data-index="${index}" title="Duplicar bocadillo (para repartir frases entre speakers)">
          <span class="material-symbols-outlined text-[18px]">content_cut</span>
        </button>
        <button type="button" class="delete-bubble-btn flex items-center justify-center w-8 h-8 rounded-full hover:bg-error/15 text-error transition-all" data-index="${index}" title="Borrar bocadillo">
          <span class="material-symbols-outlined text-[18px]">delete</span>
        </button>`
      : '';

    const playBtnHtml = state.isEditMode
      ? `<button type="button" class="play-bubble-btn flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-secondary/10 hover:bg-secondary/20 text-secondary transition-all mt-0.5" data-index="${index}" title="Escuchar este trozo (pulsa otra vez para parar)">
          <span class="material-symbols-outlined text-[16px]">volume_up</span>
        </button>`
      : '';

    div.innerHTML = `
      ${checkboxHtml}
      <div class="flex-shrink-0 w-10 h-10 rounded-full ${bg} flex items-center justify-center text-white font-bold shadow-md select-none">${escapeHtml(initial)}</div>
      <div class="flex-1 max-w-3xl ${alignment === 'right' ? 'text-right' : ''}">
        <div class="flex items-center gap-2 mb-2 ${alignment === 'right' ? 'justify-end' : ''}">
          ${alignment === 'right' ? `<span class="font-label-sm text-outline select-none">${formatTime(para.start)}</span>` : ''}
          <span class="font-label-md ${txt} font-bold select-none">${escapeHtml(speakerName)}</span>
          ${editActionsHtml}
          ${alignment === 'left' ? `<span class="font-label-sm text-outline select-none">${formatTime(para.start)}</span>` : ''}
        </div>
        <div class="surface_glass border border-outline-variant/30 p-4 rounded-2xl ${alignment === 'right' ? `rounded-tr-none border-r-4 ${border} text-left` : `rounded-tl-none border-l-4 ${border}`} shadow-premium relative">
          <div class="flex items-start gap-2 ${alignment === 'right' ? 'flex-row-reverse' : ''}">
            ${playBtnHtml}
            <p class="text-on-surface font-body-md leading-relaxed flex-1 ${state.isEditMode ? 'bubble-editable rounded px-1' : ''}"
               ${state.isEditMode ? 'contenteditable="true"' : ''}
               data-index="${index}">${escapeHtml(para.text)}</p>
          </div>
        </div>
      </div>
    `;

    if (state.isEditMode) {
      const p = div.querySelector('p');
      if (p) {
        p.addEventListener('blur', (e) => {
          const target = e.target as HTMLElement;
          const newText = target.innerText;
          if (state.paragraphs[index].text !== newText) {
            saveUndoState();
            state.paragraphs[index].text = newText;
            saveTranscript();
          }
        });
        p.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && e.ctrlKey) p.blur();
        });
      }

      const cb = div.querySelector<HTMLInputElement>('.select-bubble-cb');
      if (cb) {
        cb.addEventListener('change', () => {
          if (cb.checked) state.selectedBubbles.add(index);
          else state.selectedBubbles.delete(index);
          renderTranscript();
        });
      }

      const reassignBtn = div.querySelector<HTMLButtonElement>('.reassign-bubble-btn');
      if (reassignBtn) {
        reassignBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          showSpeakerPicker(
            reassignBtn,
            { exclude: para.speaker, allowNew: true, newLabel: 'Nuevo speaker' },
            (newSpeaker) => reassignBubble(index, newSpeaker)
          );
        });
      }

      const deleteBtn = div.querySelector<HTMLButtonElement>('.delete-bubble-btn');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          void deleteBubble(index);
        });
      }

      const playBtn = div.querySelector<HTMLButtonElement>('.play-bubble-btn');
      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          playSegment(index, para.start, effectiveEnd(index));
        });
      }

      const splitBtn = div.querySelector<HTMLButtonElement>('.split-bubble-btn');
      if (splitBtn) {
        splitBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          splitBubble(index);
        });
      }
    }

    container.appendChild(div);
  });
}

export function mergeBubbles(sourceIndex: number, targetIndex: number): void {
  if (
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= state.paragraphs.length ||
    targetIndex >= state.paragraphs.length
  ) {
    return;
  }
  saveUndoState();
  const upper = Math.min(sourceIndex, targetIndex);
  const lower = Math.max(sourceIndex, targetIndex);
  const merged = `${state.paragraphs[upper].text} ${state.paragraphs[lower].text}`;
  state.paragraphs[targetIndex].text = merged;
  state.paragraphs.splice(sourceIndex, 1);
  state.selectedBubbles.clear();
  saveTranscript();
  renderTranscript();
}

export function renderSpeakers(): void {
  const list = $('speakersList');
  list.innerHTML = '';

  const speakers = Object.keys(state.speakerMap)
    .map(Number)
    .sort((a, b) => a - b);
  for (const speaker of speakers) {
    const colorIdx = colorIndexFor(speaker);
    const bg = SPEAKER_BG[colorIdx];
    const name = state.speakerMap[speaker] ?? `Speaker ${speaker}`;
    const canMerge = speakers.length > 1;

    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-center gap-2';
    wrapper.innerHTML = `
      <div class="w-3 h-3 rounded-full ${bg} flex-shrink-0"></div>
      <input type="text"
             value="${escapeHtml(name)}"
             data-speaker="${speaker}"
             class="speaker-name-input flex-1 min-w-0 text-left p-2 rounded-lg border border-outline-variant hover:bg-surface-container-low focus:bg-white focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all text-sm font-bold"
             placeholder="Speaker ${speaker}">
      ${
        canMerge
          ? `<button type="button" class="merge-speaker-btn flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg hover:bg-outline-variant/30 text-on-surface-variant transition-all" data-speaker="${speaker}" title="Fusionar con otro speaker">
              <span class="material-symbols-outlined text-base">merge</span>
            </button>`
          : ''
      }
    `;

    const input = wrapper.querySelector<HTMLInputElement>('.speaker-name-input');
    if (input) {
      const commit = (): void => {
        const newName = input.value.trim() || `Speaker ${speaker}`;
        input.value = newName;
        if (state.speakerMap[speaker] !== newName) {
          state.speakerMap[speaker] = newName;
          saveTranscript();
          renderTranscript();
        }
      };
      input.addEventListener('blur', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          input.blur();
        }
        if (e.key === 'Escape') {
          input.value = state.speakerMap[speaker] ?? `Speaker ${speaker}`;
          input.blur();
        }
      });
    }

    const mergeBtn = wrapper.querySelector<HTMLButtonElement>('.merge-speaker-btn');
    if (mergeBtn) {
      mergeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        showSpeakerPicker(mergeBtn, { exclude: speaker, allowNew: false }, (target) => {
          void mergeSpeakers(speaker, target);
        });
      });
    }

    list.appendChild(wrapper);
  }
}
